<template>
  <div
    v-if="product !== null"
    class="product-item md-elevation-2"
    @click.prevent="navigateToProduct(product.id)"
  >
    <div class="product-title mb-3">
      {{ product.name }}
    </div>

    <div class="badge-container mb-3">
      <div class="md-chip md-chip-primary mr-2 mb-2">
        REKLAM
      </div>
      <div class="md-chip md-chip-outlined mr-2 mb-2">
        <img src="@/assets/images/icons/list-order.png" class="md-chip-icon">
        {{ product.type }}{{ buildLongId(product.id) }}
      </div>

      <div class="md-chip md-chip-outlined mr-2 mb-2">
        <img src="@/assets/images/icons/calendar.png" class="md-chip-icon">
        {{ $t('dashboardCompany.key', {moment: getMoment(product.logTime)}) }}
      </div>
    </div>

    <div class="d-flex justify-content-between align-items-center mt-3">
      <div class="d-flex align-items-center product-author">
        <img
          :src="getPhoto(product.user.photo,'thumbnailCropped')"
          class="md-avatar mr-2"
        >
        <div>
          <span class="name">{{ product.user.name }}  {{ product.user.surname }}</span> <br>
          <span class="text-secondary company-name">
            {{ product.company.name }}
            {{ getCompanyFormat(product.company.format) }}
          </span>
        </div>
      </div>
      <div>
        <div class="d-flex align-items-center">
          <span
            v-if="product.status === 'ACTIVE'"
            class="md-chip md-chip-success mr-2"
          >Aktiv</span>
        </div>
      </div>
    </div>

    <p class="product-description mt-3">
      {{ product.description }}
    </p>
  </div>
</template>

<script>
import { buildLongId, getCompanyFormat, getPhoto } from "@/repositories/Utils";
import moment from "moment";

export default {
  name: 'ProductItem',
  props: {
    product: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  methods: {
    getMoment(time) {
      moment.locale(this.$i18n.locale);
      return moment(new Date(time)).format('DD MMM YYYY');
    },
    buildLongId(id) {
      return buildLongId(id, 5);
    },
    getCompanyFormat(keyword) {
      return getCompanyFormat(keyword, this.$i18n.locale);
    },
    getPhoto(path, size) {
      return getPhoto(path, size);
    },
    navigateToProduct(productId) {
      this.$router.push(`/dashboard/product/${productId}`);
    }
  }
}
</script>

<style lang="scss" scoped>
// Material Design Variables
$md-primary: #1565C0;
$md-primary-dark: #0D47A1;
$md-primary-light: #90CAF9;
$md-secondary: #C2185B;
$md-success: #2E7D32;
$md-warning: #F57F17;
$md-error: #C62828;
$md-surface: #FFFFFF;
$md-background: #F5F5F5;
$md-on-primary: #FFFFFF;
$md-on-secondary: #FFFFFF;
$md-on-surface: #212121;
$md-on-background: #212121;

// Material Design Typography
$md-font-family: Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
$md-font-weight-regular: 400;
$md-font-weight-medium: 500;
$md-font-weight-bold: 700;

// Material Design Elevation
.md-elevation-2 {
  border-radius: 8px;
  box-shadow: 0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12);
}

// Material Design Chips
.md-chip {
  display: inline-flex;
  align-items: center;
  padding: 0 12px;
  height: 32px;
  font-size: 14px;
  font-weight: $md-font-weight-medium;
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &-icon {
    margin-right: 8px;
    width: 16px;
    height: 16px;
  }

  &-primary {
    background-color: $md-primary;
    color: $md-on-primary;
  }

  &-outlined {
    background-color: transparent;
    border: 1px solid rgba(0, 0, 0, 0.12);
    color: $md-on-surface;
  }

  &-success {
    background-color: $md-success;
    color: $md-on-primary;
  }

  &-warning {
    background-color: $md-warning;
    color: $md-on-surface;
  }

  &-error {
    background-color: $md-error;
    color: $md-on-primary;
  }
}

// Material Design Avatar
.md-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
}

// Component Styles
.product-item {
  cursor: pointer;
  padding: 20px;
  background-color: $md-surface;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: $md-font-family;

  .product-title {
    font-size: 20px;
    font-weight: $md-font-weight-medium;
    color: $md-on-surface;
    line-height: 1.5;
    letter-spacing: 0.15px;
  }

  .badge-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: 8px 0;
  }

  .product-description {
    color: rgba($md-on-surface, 0.7);
    line-height: 1.5;
    letter-spacing: 0.25px;
    margin-top: 8px;
    font-size: 14px;
  }

  .product-author {
    .name {
      font-size: 14px;
      font-weight: $md-font-weight-medium;
      color: $md-on-surface;
      letter-spacing: 0.1px;
    }

    .company-name {
      font-size: 12px;
      letter-spacing: 0.4px;
      color: rgba($md-on-surface, 0.6);
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 5px -3px rgba(0,0,0,.2), 0 8px 10px 1px rgba(0,0,0,.14), 0 3px 14px 2px rgba(0,0,0,.12);

    .product-title {
      color: $md-primary;
    }
  }

  @media (max-width: 768px) {
    .d-flex {
      flex-direction: column;
      align-items: flex-start !important;

      > div:last-child {
        margin-top: 16px;
        width: 100%;
      }
    }
  }
}
</style>
